/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/checkbox.scss';

import React, {CSSProperties, MouseEventHandler, useContext, useMemo} from 'react';
import classNames from 'classnames';
import AwesomeIcon from '../icon/awesomeIcon';
import {convertColor, editColor, findDarkestColor} from "../util/colorUtil";
import {ControlContext} from "../util/controlContext";
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {ErrorType} from "../../../index";

interface Props {
    onCheck: MouseEventHandler;
    onMouseEnter: MouseEventHandler;
    onMouseLeave: MouseEventHandler;
    checked: boolean | 'partial';
    disabled: boolean;
    isHovered: boolean;
    label?: string;
    labelPosition: 'left' | 'right';
    className?: string;
    labelClassName?: string;
    checkboxClassName?: string;
    checkClassName?: string;
    errorClassName?: string;
    id: string;
    error: ErrorType;
    checkIcon: IconName | React.ReactNode;
    baseColor?: string;
    contrastColor?: string;
    title?: string;
    useDarkestColor: boolean;
    asRadio?: boolean;
}

export default function DvrdCheckbox(props: Props) {
    const context = useContext(ControlContext);
    const {
        disabled, error, label, useDarkestColor, isHovered, id, onCheck, onMouseEnter, onMouseLeave, title,
        labelPosition, checkIcon, checked, asRadio
    } = props;
    const className = useMemo(() => {
        const classes: Array<string> = ['checkboxContainer'];
        if (disabled) classes.push('disabled');
        if (!!error) classes.push('error');
        if (!label?.length) classes.push('no-label');
        return classNames(classes, props.className);
    }, [disabled, error, label, props.className]);
    const labelClassName = useMemo(() => {
        const classes: Array<string> = ['checkboxLabel'];
        if (disabled) classes.push('disabled');
        if (!!error) classes.push('error');
        return classNames(classes, props.labelClassName);
    }, [disabled, error, props.labelClassName]);
    const checkboxClassName = useMemo(() => {
        const classes: Array<string> = ['checkbox'];
        if (disabled) classes.push('disabled');
        if (asRadio) classes.push('as-radio');
        return classNames(classes, props.checkboxClassName);
    }, [disabled, props.checkClassName]);
    const checkClassName = useMemo(() => {
        const classes = ['checkbox-check'];
        if (disabled) classes.push('disabled');
        if (checked) classes.push('checked');
        return classNames(classes, props.checkClassName);
    }, [disabled, checked, props.checkClassName]);
    const [baseColor, contrastColor] = useMemo(() => {
        const base = convertColor(props.baseColor || context.baseColor)
        const contrast = convertColor(props.baseColor || context.baseColor);
        return [base, contrast];
    }, [props.baseColor, context.baseColor, props.contrastColor, context.contrastColor]);
    const checkboxStyle: CSSProperties = useMemo(() => {
        let color: string;
        if (!!error) color = 'red';
        else if (disabled) color = 'color-gray-4';
        else {
            if (useDarkestColor)
                color = findDarkestColor(baseColor, contrastColor);
            else color = baseColor;
            if (isHovered) color = editColor(-.2, color);
        }
        return {borderColor: convertColor(color)};
    }, [error, useDarkestColor, isHovered, disabled, baseColor, contrastColor]);
    const [checkStyle, partialCheckStyle]: [CSSProperties, CSSProperties] = useMemo(() => {
        let color: string = baseColor;
        if (!!error) color = 'red';
        else if (disabled) color = 'color-gray-4';
        else if (useDarkestColor) color = findDarkestColor(baseColor, contrastColor);
        return [{color: convertColor(color)}, {backgroundColor: convertColor(color)}];
    }, [error, disabled, useDarkestColor, baseColor, contrastColor]);
    const mainColor = useMemo(() => {
        return useDarkestColor ? findDarkestColor(baseColor, contrastColor) : baseColor;
    }, [useDarkestColor, props.baseColor, context.baseColor, props.contrastColor, context.contrastColor]);

    function renderLabel(position: 'left' | 'right'): React.ReactNode {
        if (!label || labelPosition !== position) return null;
        return <label className={classNames(labelClassName, position)}>{label}</label>
    }

    function renderCheckbox() {
        return (
            <div className={checkboxClassName} style={checkboxStyle}>
                <div className='ripple' style={{backgroundColor: mainColor}}/>
                {renderCheck()}
            </div>
        )
    }

    function renderCheck() {
        if (React.isValidElement(checkIcon)) return React.cloneElement((checkIcon as React.ReactElement<any>), {
            className: classNames((checkIcon as React.ReactElement<any>).props.className, checkClassName),
            style: {...(checkIcon as React.ReactElement<any>).props.style, ...checkStyle}
        });
        else if (checked === 'partial') return (
            <div className={classNames(checkClassName, 'partial-icon')} style={partialCheckStyle}/>
        );
        let checkName: IconName = 'check';
        let additionalClassName: string | null = null;
        if (typeof checkIcon === 'string') checkName = checkIcon as IconName;
        else if (asRadio) checkName = 'circle';
        return <AwesomeIcon name={checkName} className={classNames(checkClassName, additionalClassName)}
                            style={checkStyle}/>;
    }

    function renderError() {
        return <label className={classNames('error', props.errorClassName)}>{error}</label>;
    }

    return (
        <div className={className} id={id} onClick={onCheck} onMouseEnter={onMouseEnter}
             title={title} onMouseLeave={onMouseLeave}>
            {renderLabel('left')}
            {renderCheckbox()}
            {renderLabel('right')}
            {renderError()}
        </div>
    )
}